/**
 * @file SiteBuilderPage.tsx
 * @description «Сайт → Конструктор сайта» — одно окно для страниц, блоков и
 * вида сайта (phase19.16 Р1-6а, §7.14–§7.16 фазы; проект
 * docs/phases/phase19.16-materials/uiux-builder-shell.md).
 *
 * Слева страницы и структура (шапка — блоки — подвал) и разделы «Весь
 * сайт», в центре настоящая витрина в режиме черновика, справа настройки
 * выбранного. Выбор — в адресе. Меню кабинета спрятано в шторку (≡),
 * конструктор занимает всю ширину окна, шёлкового фона нет (SB78).
 * Черновик вида сайта, права и общие окна — каркас `SiteViewLayout`.
 * phase19.16 Р2: окна «Добавить блок» (заготовки) и «Сделать главной».
 * phase19.16 Р3а: окна черновика страницы (`PagePublicationsHost`).
 * С 1024 прокрутка всей страницы запрещена (`useLockedPageScroll`), на
 * 768–1023 и телефоне — как обычно.
 */

import { useEffect } from "react";

import { useBuilder, BuilderContext } from "../../../components/site-builder/builder-context";
import { BuilderPhone } from "../../../components/site-builder/BuilderPhone";
import { BuilderWorkspace } from "../../../components/site-builder/BuilderWorkspace";
import { AddPageDialog } from "../../../components/site-builder/dialogs/AddPageDialog";
import { SaveDesignDialog } from "../../../components/site-builder/designs/SaveDesignDialog";
import { SetHomeHost } from "../../../components/site-builder/dialogs/SetHomeHost";
import { PagePublicationsHost } from "../../../components/site-builder/publish/PagePublicationsHost";
import { PresetLibraryHost } from "../../../components/site-builder/section/PresetLibraryHost";
import { BuilderTopBar } from "../../../components/site-builder/top/BuilderTopBar";
import { SitePreviewDialog } from "../../../components/site-view/preview/SitePreviewDialog";
import { useSiteView } from "../../../components/site-view/site-view-context";
import { ConfirmDialog } from "../../../components/ui/ConfirmDialog";
import { ListError } from "../../../components/ui/ListStates";
import { useFluidLayout } from "../../../components/ui/AppShell";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { usePlainBackdrop } from "../../../components/ui/use-plain-backdrop";
import { useBleedLayout, useDrawerMenu } from "../../../components/ui/use-shell-mode";
import { useLockedPageScroll } from "../../../hooks/site-builder/useLockedPageScroll";
import { LG_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";
import { pageScrollLocked } from "../../../lib/site-builder/workspace-area";
import { useSiteBuilderState } from "../../../hooks/site-builder/useSiteBuilderState";

/** Старый адрес «Страницы сайта» (`?panel=pages`) — открыть главную. */
function useLegacyPagesPanel() {
  const b = useBuilder();
  const legacy = b.selection.panel === "pages";
  const { select } = b;
  useEffect(() => {
    if (legacy) void select({ panel: null });
  }, [legacy, select]);
}

function BuilderBody() {
  const b = useBuilder();
  const view = useSiteView();
  useLegacyPagesPanel();
  // С 1024 (область закреплена): страница целиком не прокручивается — только структура, канва и панель.
  const lg = useMediaQuery(LG_QUERY);
  useLockedPageScroll(pageScrollLocked(b.layout, lg));
  if (view.error && !view.draft) {
    return (
      <div className="p-4">
        <ListError message={view.error} onRetry={() => void view.reload()} />
      </div>
    );
  }
  return b.layout === "phone" ? <BuilderPhone /> : <BuilderWorkspace />;
}

export function SiteBuilderPage() {
  useDrawerMenu();
  // Вся ширина окна (SB104): шапка кабинета без предела 1600 и содержимое без полей.
  useFluidLayout();
  useBleedLayout();
  usePlainBackdrop();
  const view = useSiteView();
  const notify = useNotify();
  const state = useSiteBuilderState();
  const { designs } = state.value;
  const draftDiffers = view.changes?.changedParts.some((p) => p.part === "THEME" || p.part === "BRAND") ?? false;

  return (
    <BuilderContext.Provider value={state.value}>
      {/* До 1024 статусы и «что открыто» — строкой содержимого; у конструктора нет полей каркаса. */}
      <div className="px-4 pt-3 empty:hidden">
        <BuilderTopBar />
      </div>
      <BuilderBody />
      <AddPageDialog open={state.addPageOpen} onClose={() => state.setAddPageOpen(false)} />
      <SaveDesignDialog
        open={state.saveDesignOpen}
        draftDiffers={draftDiffers}
        onClose={() => state.setSaveDesignOpen(false)}
        onSubmit={async (input) => {
          // Ошибку сервера окно покажет само (designErrorText).
          await designs.create(input);
          notify.success(`Дизайн «${input.name}» сохранён`);
        }}
      />
      <PresetLibraryHost library={state.library} onClose={state.closeLibrary} />
      <SetHomeHost pageId={state.setHomePageId} onClose={state.closeSetHome} />
      {/* Р3а: «Публикации» шторкой, «Все версии», «Вернуть версию», «Удалённые страницы». */}
      <PagePublicationsHost ui={state.publicationsUi} />
      <ConfirmDialog request={state.confirmApi.request} onSettle={state.confirmApi.settle} />
      {/* «Весь сайт ↗», пока на канве дизайн или версия: тот же снимок на весь экран. */}
      <SitePreviewDialog
        open={state.wholeSite !== null}
        onClose={state.closeWholeSite}
        title={state.wholeSite?.label}
        source={state.wholeSite?.source}
      />
    </BuilderContext.Provider>
  );
}
